import "./scss/global.scss";

import App from "./App.vue";
import {buildRouter} from "./router";
import {OAuthPlugin} from "./auth/oauth";
import {createI18n, type I18n} from "vue-i18n";
import * as translations from "./locales/translations.json";
import * as translationsEn from "./locales/translations_en.json";
import * as translationsRu from "./locales/translations_ru.json";
import {createApp} from "vue";
import axios, {type InternalAxiosRequestConfig} from "axios";
import {interceptHttpResponses} from "@/interceptor/http.interceptor";
import {AuthMethod} from "@/model/auth-method";
import {Language} from "@/model/language";
import {getRedirectPath, removeRedirectPath} from "@/auth/redirect";
import PrimeVue from "primevue/config";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import Checkbox from "primevue/checkbox";
import FileUpload from "primevue/fileupload";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Paginator from "primevue/paginator";
import Tooltip from "primevue/tooltip";
import DatePicker from "primevue/datepicker";
import Menu from "primevue/menu";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import ToggleSwitch from "primevue/toggleswitch";
import MultiSelect from "primevue/multiselect";
import AutoComplete from "primevue/autocomplete";
import Badge from "primevue/badge";
import ToastService from "primevue/toastservice";
import {preset} from "@/preset";
import type {AppDefinition} from "@/app";
import {InputNumber} from "primevue";

export const i18n: I18n = createI18n({
  locale: Language.ENG,
  fallbackLocale: Language.ENG,
  legacy: false,
  messages: {
    [Language.RUS]: translationsRu.ru,
    [Language.EST]: translations.ee,
    [Language.ENG]: translationsEn.en,
  },
});

const app = createApp(App) as AppDefinition;
const router = buildRouter(app, i18n);

app.use(PrimeVue, {
  theme: {
    preset: preset,
    options: {
      prefix: 'p',
      darkModeSelector: false,
      cssLayer: false
    }
  }
});

app.use(router);

app.component("Dialog", Dialog);
app.component("Badge", Badge);
app.component("Button", Button);
app.component("InputText", InputText);
app.component("InputNumber", InputNumber);
app.component("Textarea", Textarea);
app.component("Select", Select);
app.component("Checkbox", Checkbox);
app.component("FileUpload", FileUpload);
app.component("Card", Card);
app.component("DataTable", DataTable);
app.component("Column", Column);
app.component("Paginator", Paginator);
app.component("DatePicker", DatePicker);
app.component("Menu", Menu);
app.component("Tabs", Tabs);
app.component("TabList", TabList);
app.component("Tab", Tab);
app.component("TabPanels", TabPanels);
app.component("TabPanel", TabPanel);
app.component("ToggleSwitch", ToggleSwitch);
app.component("MultiSelect", MultiSelect);
app.component("AutoComplete", AutoComplete);

app.directive("tooltip", Tooltip);

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_INTERNAL_API,
  withCredentials: true,
});

app.provide("axios", axiosInstance);

app.use({
  install() {
    axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const authService = app.config.globalProperties.$auth;

      if (authService?.isAuthenticated()) {
        config.headers["Authorization"] = `Bearer ${authService.getToken()}`;
      }

      setDefaultRequestHeader(config, "Content-Type", "application/json; charset=utf-8");
      setDefaultRequestHeader(config, "Accept", "application/json");
      return config;
    });

    app.config.globalProperties.$axios = axiosInstance;
  },
});

const setDefaultRequestHeader = (config: InternalAxiosRequestConfig, key: string, value: string) => {
  if (config.headers && !config.headers[key]) {
    config.headers[key] = value;
  }
};

app.use({
  install() {
    (function (d, s, id) {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id) || fjs.parentNode === null) {
        return;
      }
      const js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.src = "https://connect.facebook.net/et_EE/sdk.js#xfbml=1&version=v8.0";
      fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  },
});

app.use(OAuthPlugin, {
  onLogin: async () => {
    const redirectPath = getRedirectPath();
    if (redirectPath) {
      removeRedirectPath();
      await router.push({path: redirectPath});
      return;
    }

    await router.push({name: "Groups"});
  },
  onLogout: async (authMethod: AuthMethod, token: string) => {
    if (authMethod === AuthMethod.UAA) {
      const resp = await axiosInstance.post("/openid/logout", {token: token});
      location.href = resp.data.redirectUri;
      return;
    }

    await router.push({path: "/"});
  },
  axios: axiosInstance
});

app.use(i18n);
app.use(ToastService);
app.mount('#app');

interceptHttpResponses(app, i18n, axiosInstance);
